/**
 * Phase 17 — Enterprise Goods Receipt & Delivery Mock Service Layer
 * In-memory relational store with 500+ realistic records, optimistic versioning,
 * idempotency caching, SAP MIGO simulation, and pure metric aggregation.
 */

import {
  GoodsReceiptRecord,
  DeliveryTrackingRecord,
  GrnItemRecord,
  GrnMetricsSummary,
  GrnFilterState,
  CreateGrnInput,
  QualityInspectionItemInput,
  VendorDeliveryPerformance,
} from '@/types/domain/grn-management';
import { MOCK_PURCHASE_ORDERS, MOCK_GOODS_RECEIPTS } from '@/mocks/database';
import { env } from '@/config/env';
import { notifyGlobal } from '@/context/NotificationContext';
import {
  calculateAcceptedQuantity,
  calculatePendingQuantity,
  calculateRejectionRate,
  detectDeliveryDelay,
  deriveDeliveryStatus,
  deriveOverallQualityStatus,
  calculateVendorPerformance,
} from '@/features/grn/utils/grnCalculations';

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const GENERATED_GRN_RECORDS: GoodsReceiptRecord[] = MOCK_GOODS_RECEIPTS.map((grn, i) => {
  const po = MOCK_PURCHASE_ORDERS[i % MOCK_PURCHASE_ORDERS.length]!;
  const isFirst = i === 0 || grn.grnNumber === 'GRN-6001';
  const isSecond = i === 1 || grn.grnNumber === 'GRN-6002';
  const isThird = i === 2 || grn.grnNumber === 'GRN-6003';

  // Seed item quantities
  const items: GrnItemRecord[] = grn.items.map((it, itemIdx) => {
    let ordered = it.orderedQuantity;
    let received = it.receivedQuantity;
    let rejected = it.rejectedQuantity;

    if (isFirst) {
      ordered = 10;
      received = 10;
      rejected = 0;
    } else if (isSecond) {
      ordered = 2;
      received = 1;
      rejected = 1;
    } else if (isThird) {
      ordered = 5;
      received = 5;
      rejected = 0;
    } else if (i % 7 === 0) {
      // Periodic partial receipt
      received = Math.floor(ordered * 0.6);
      rejected = Math.floor(received * 0.1);
    } else if (i % 11 === 0) {
      // Periodic rejection
      rejected = Math.floor(received * 0.25);
    }

    const accepted = calculateAcceptedQuantity(received, rejected);
    const pending = calculatePendingQuantity(ordered, accepted);

    return {
      id: `grn-item-${grn.grnNumber}-${itemIdx + 1}`,
      grnId: grn.id,
      poItemId: `po-item-${po.id}-${itemIdx + 1}`,
      lineNumber: itemIdx + 1,
      materialCode: it.materialCode || `MAT-100${itemIdx + 1}`,
      description: it.materialName || 'Industrial IT Hardware',
      orderedQuantity: ordered,
      receivedQuantity: received,
      rejectedQuantity: rejected,
      acceptedQuantity: accepted,
      pendingQuantity: pending,
      uom: it.unitOfMeasure || 'EA',
      batchNumber: `BATCH-2026-${100 + i}`,
      storageLocation: i % 2 === 0 ? 'SLOC-RAW-01' : 'SLOC-MAIN-02',
      qualityStatus: rejected > 0 ? (accepted > 0 ? 'PARTIALLY_PASSED' : 'FAILED') : 'PASSED',
      rejectionReason: rejected > 0 ? (i % 2 === 0 ? 'QUALITY_FAILURE' : 'DAMAGED') : undefined,
      rejectionNotes: rejected > 0 ? 'Visual surface inspection failed warehouse threshold.' : undefined,
      unitPrice: 500,
      taxPercent: 18,
      lineTotal: accepted * 500 * 1.18,
    };
  });

  const totalOrdered = items.reduce((sum, item) => sum + item.orderedQuantity, 0);
  const totalReceived = items.reduce((sum, item) => sum + item.receivedQuantity, 0);
  const totalRejected = items.reduce((sum, item) => sum + item.rejectedQuantity, 0);
  const totalAccepted = items.reduce((sum, item) => sum + item.acceptedQuantity, 0);
  const totalPending = items.reduce((sum, item) => sum + item.pendingQuantity, 0);
  const rejectionRate = calculateRejectionRate(totalReceived, totalRejected);

  let status: GoodsReceiptRecord['status'] = 'ACCEPTED';
  if (isSecond) {
    status = 'PARTIALLY_ACCEPTED';
  } else if (i % 13 === 0) {
    status = 'QUALITY_HOLD';
  } else if (totalRejected > 0 && totalAccepted === 0) {
    status = 'REJECTED';
  } else if (totalRejected > 0) {
    status = 'PARTIALLY_ACCEPTED';
  }

  const sapSyncStatus: GoodsReceiptRecord['sapSyncStatus'] =
    isThird ? 'PENDING_SYNC' : i % 8 === 0 ? 'PENDING_SYNC' : 'SYNCED';

  return {
    id: grn.id,
    grnNumber: grn.grnNumber,
    poId: po.id,
    poNumber: po.poNumber,
    sapPoNumber: po.sapPoNumber || `450099${80 + (i % 20)}`,
    vendorId: po.vendorId,
    vendorName: po.vendorName,
    vendorCode: `VND-${po.vendorId.toUpperCase()}`,
    plantCode: grn.plantCode || 'PLANT-1000',
    plantName: 'Austin High-Tech Assembly Plant 1000',
    storageLocation: 'SLOC-RAW-01',
    unloadingDock: `Bay-${(i % 5) + 1}`,
    deliveryChallanNumber: `DC-2026-${8800 + i}`,
    carrierName: i % 2 === 0 ? 'FedEx Logistics' : 'Blue Dart Freight',
    trackingNumber: `TRK-8899${1000 + i}`,
    receivedDate: grn.receivedDate || new Date(Date.now() - (i % 30) * 86400000).toISOString().slice(0, 10),
    inspectionDate: grn.receivedDate || new Date().toISOString().slice(0, 10),
    inspectorName: grn.inspectorName || 'Quality Lead John Miller',
    receiverName: 'Marcus Vance (Warehouse Supervisor)',
    receiverId: 'usr-wh-01',
    status,
    qualityStatus: deriveOverallQualityStatus(totalReceived, totalAccepted, totalRejected, status === 'QUALITY_HOLD'),
    items,
    totalOrderedQuantity: totalOrdered,
    totalReceivedQuantity: totalReceived,
    totalAcceptedQuantity: totalAccepted,
    totalRejectedQuantity: totalRejected,
    totalPendingQuantity: totalPending,
    rejectionRatePercent: rejectionRate,
    sapGrnNumber: grn.sapGrnNumber || `500090${6001 + i}`,
    sapSyncStatus,
    sapMovementType: '101', // SAP standard goods receipt for PO into warehouse
    threeWayMatchStatus:
      totalPending > 0 || totalRejected > 0
        ? 'QUANTITY_MISMATCH'
        : i % 9 === 0
        ? 'INVOICE_PENDING'
        : 'MATCHED',
    invoiceId: `inv-700${(i % 50) + 1}`,
    invoiceNumber: `INV-700${(i % 50) + 1}`,
    documentsCount: (i % 3) + 1,
    commentsCount: (i % 4),
    auditHistory: [
      {
        id: `aud-${grn.id}-1`,
        grnId: grn.id,
        timestamp: new Date(Date.now() - 86400000 * 2).toISOString(),
        eventType: 'GRN_CREATED',
        actorId: 'usr-wh-01',
        actorName: 'Marcus Vance',
        actorRole: 'Warehouse Receiver',
        details: `Initial dock receipt registered against PO #${po.poNumber}.`,
        systemSource: 'ProcureAI',
      },
      {
        id: `aud-${grn.id}-2`,
        grnId: grn.id,
        timestamp: new Date(Date.now() - 86400000).toISOString(),
        eventType: 'INSPECTION_RECORDED',
        actorId: 'usr-qa-02',
        actorName: 'John Miller',
        actorRole: 'Quality Inspector',
        details: `Inspected ${totalReceived} units. Accepted: ${totalAccepted}, Rejected: ${totalRejected}.`,
        systemSource: 'ProcureAI',
      },
      ...(sapSyncStatus === 'SYNCED'
        ? [
            {
              id: `aud-${grn.id}-3`,
              grnId: grn.id,
              timestamp: new Date().toISOString(),
              eventType: 'SAP_SYNC_SUCCESS' as const,
              actorId: 'SAP_BAPI',
              actorName: 'SAP MIGO Service',
              actorRole: 'Integration Service',
              details: `Posted to SAP S/4HANA MIGO document #${grn.sapGrnNumber || '5000906001'}.`,
              systemSource: 'SAP_BAPI' as const,
            },
          ]
        : []),
    ],
    notes: 'Standard receiving and visual QA conducted at loading bay.',
    version: 1,
    createdAt: grn.createdAt || new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
});

class MockGoodsReceiptService {
  private grns: GoodsReceiptRecord[] = [...GENERATED_GRN_RECORDS];
  private idempotencyCache = new Map<string, GoodsReceiptRecord>();

  private findInternalRecord(id: string): GoodsReceiptRecord | undefined {
    return this.grns.find((g) => g.id === id || g.grnNumber === id);
  }

  syncGrnRecord(grn: GoodsReceiptRecord, previousId?: string): void {
    const idx = this.grns.findIndex(
      (g) => g.id === grn.id || g.grnNumber === grn.grnNumber || (previousId && g.id === previousId)
    );
    if (idx >= 0) {
      this.grns[idx] = { ...this.grns[idx], ...grn };
    } else {
      this.grns.unshift(grn);
    }
  }

  async getGoodsReceipts(
    filters: GrnFilterState
  ): Promise<{ items: GoodsReceiptRecord[]; total: number; page: number; pageSize: number }> {
    await delay(env.mockDelayMs);
    let results = [...this.grns];

    if (filters.search) {
      const q = filters.search.toLowerCase().trim();
      results = results.filter(
        (g) =>
          g.grnNumber.toLowerCase().includes(q) ||
          g.poNumber.toLowerCase().includes(q) ||
          (g.sapGrnNumber && g.sapGrnNumber.toLowerCase().includes(q)) ||
          g.vendorName.toLowerCase().includes(q) ||
          g.vendorCode.toLowerCase().includes(q) ||
          g.plantCode.toLowerCase().includes(q) ||
          g.items.some((it) => it.materialCode.toLowerCase().includes(q) || it.description.toLowerCase().includes(q))
      );
    }

    if (filters.status && filters.status !== 'ALL') {
      results = results.filter((g) => g.status === filters.status);
    }

    if (filters.qualityStatus && filters.qualityStatus !== 'ALL') {
      results = results.filter((g) => g.qualityStatus === filters.qualityStatus);
    }

    if (filters.plant && filters.plant !== 'ALL') {
      results = results.filter((g) => g.plantCode === filters.plant);
    }

    if (filters.vendor && filters.vendor !== 'ALL') {
      results = results.filter((g) => g.vendorId === filters.vendor || g.vendorCode === filters.vendor);
    }

    // Sort
    const sortBy = filters.sortBy || 'receivedDate';
    const sortOrder = filters.sortOrder || 'desc';
    results.sort((a, b) => {
      let valA: string | number = a[sortBy];
      let valB: string | number = b[sortBy];
      if (sortBy === 'receivedDate') {
        valA = new Date(valA as string).getTime();
        valB = new Date(valB as string).getTime();
      }
      if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
      if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    const page = filters.page || 1;
    const pageSize = filters.pageSize || 25;
    const startIndex = (page - 1) * pageSize;
    const paginatedItems = results.slice(startIndex, startIndex + pageSize);

    return {
      items: JSON.parse(JSON.stringify(paginatedItems)),
      total: results.length,
      page,
      pageSize,
    };
  }

  async getGoodsReceiptById(id: string): Promise<GoodsReceiptRecord | null> {
    await delay(env.mockDelayMs);
    const grn = this.findInternalRecord(id);
    if (!grn) return null;
    return JSON.parse(JSON.stringify(grn));
  }

  async getGrnMetricsSummary(): Promise<GrnMetricsSummary> {
    await delay(env.mockDelayMs);
    const totalGrns = this.grns.length;
    let totalOrdered = 0;
    let totalReceived = 0;
    let totalAccepted = 0;
    let totalRejected = 0;
    let totalPending = 0;
    let sapSyncedCount = 0;
    let qualityHoldCount = 0;

    this.grns.forEach((g) => {
      totalOrdered += g.totalOrderedQuantity;
      totalReceived += g.totalReceivedQuantity;
      totalAccepted += g.totalAcceptedQuantity;
      totalRejected += g.totalRejectedQuantity;
      totalPending += g.totalPendingQuantity;
      if (g.sapSyncStatus === 'SYNCED') sapSyncedCount++;
      if (g.status === 'QUALITY_HOLD') qualityHoldCount++;
    });

    const onTimeDeliveryRate = 92.4;
    const qualityAcceptanceRate = totalReceived > 0
      ? Number(((totalAccepted / totalReceived) * 100).toFixed(1))
      : 100;
    const rejectionRate = totalReceived > 0
      ? Number(((totalRejected / totalReceived) * 100).toFixed(1))
      : 0;

    return {
      totalGrns,
      openDeliveries: 142,
      dueToday: 18,
      overdueDeliveries: 27,
      partiallyDelivered: 45,
      fullyDelivered: totalGrns - 45,
      totalOrderedQuantity: totalOrdered,
      totalReceivedQuantity: totalReceived,
      totalAcceptedQuantity: totalAccepted,
      totalRejectedQuantity: totalRejected,
      totalPendingQuantity: totalPending,
      onTimeDeliveryRate,
      qualityAcceptanceRate,
      rejectionRate,
      sapSyncedGrnCount: sapSyncedCount,
      qualityHoldCount,
    };
  }

  async createGoodsReceipt(input: CreateGrnInput, idempotencyKey?: string): Promise<GoodsReceiptRecord> {
    await delay(env.mockDelayMs);

    if (idempotencyKey && this.idempotencyCache.has(idempotencyKey)) {
      return JSON.parse(JSON.stringify(this.idempotencyCache.get(idempotencyKey)!));
    }

    const nextGrnNumber = `GRN-${6001 + this.grns.length}`;
    const nextId = `grn-${6001 + this.grns.length}`;

    const items: GrnItemRecord[] = input.items.map((it, idx) => {
      const accepted = calculateAcceptedQuantity(it.receivedQuantity, it.rejectedQuantity);
      const pending = calculatePendingQuantity(it.orderedQuantity, accepted);

      return {
        id: `grn-item-${nextGrnNumber}-${idx + 1}`,
        grnId: nextId,
        poItemId: it.poItemId,
        lineNumber: idx + 1,
        materialCode: it.materialCode,
        description: it.description,
        orderedQuantity: it.orderedQuantity,
        receivedQuantity: it.receivedQuantity,
        rejectedQuantity: it.rejectedQuantity,
        acceptedQuantity: accepted,
        pendingQuantity: pending,
        uom: it.uom,
        batchNumber: it.batchNumber || `BATCH-${Date.now().toString().slice(-4)}`,
        storageLocation: it.storageLocation,
        qualityStatus: it.rejectedQuantity > 0 ? (accepted > 0 ? 'PARTIALLY_PASSED' : 'FAILED') : 'PASSED',
        rejectionReason: it.rejectionReason,
        rejectionNotes: it.rejectionNotes,
        unitPrice: it.unitPrice,
        taxPercent: it.taxPercent,
        lineTotal: accepted * it.unitPrice * (1 + it.taxPercent / 100),
      };
    });

    const totalOrdered = items.reduce((sum, item) => sum + item.orderedQuantity, 0);
    const totalReceived = items.reduce((sum, item) => sum + item.receivedQuantity, 0);
    const totalRejected = items.reduce((sum, item) => sum + item.rejectedQuantity, 0);
    const totalAccepted = items.reduce((sum, item) => sum + item.acceptedQuantity, 0);
    const totalPending = items.reduce((sum, item) => sum + item.pendingQuantity, 0);
    const rejectionRate = calculateRejectionRate(totalReceived, totalRejected);

    let status: GoodsReceiptRecord['status'] = 'ACCEPTED';
    if (totalRejected > 0 && totalAccepted === 0) {
      status = 'REJECTED';
    } else if (totalRejected > 0) {
      status = 'PARTIALLY_ACCEPTED';
    }

    const newGrn: GoodsReceiptRecord = {
      id: nextId,
      grnNumber: nextGrnNumber,
      poId: input.poId,
      poNumber: input.poNumber,
      sapPoNumber: input.sapPoNumber,
      vendorId: input.vendorId,
      vendorName: input.vendorName,
      vendorCode: input.vendorCode,
      plantCode: input.plantCode,
      plantName: input.plantName,
      storageLocation: input.storageLocation,
      unloadingDock: input.unloadingDock || 'Bay-1',
      deliveryChallanNumber: input.deliveryChallanNumber,
      carrierName: input.carrierName,
      trackingNumber: input.trackingNumber,
      receivedDate: input.receivedDate,
      inspectorName: input.inspectorName,
      receiverName: 'Marcus Vance (Warehouse Supervisor)',
      receiverId: 'usr-wh-01',
      status,
      qualityStatus: deriveOverallQualityStatus(totalReceived, totalAccepted, totalRejected),
      items,
      totalOrderedQuantity: totalOrdered,
      totalReceivedQuantity: totalReceived,
      totalAcceptedQuantity: totalAccepted,
      totalRejectedQuantity: totalRejected,
      totalPendingQuantity: totalPending,
      rejectionRatePercent: rejectionRate,
      sapSyncStatus: 'PENDING_SYNC',
      threeWayMatchStatus: totalRejected > 0 || totalPending > 0 ? 'QUANTITY_MISMATCH' : 'MATCHED',
      documentsCount: 0,
      commentsCount: 0,
      auditHistory: [
        {
          id: `aud-${nextId}-create`,
          grnId: nextId,
          timestamp: new Date().toISOString(),
          eventType: 'GRN_CREATED',
          actorId: 'usr-wh-01',
          actorName: 'Marcus Vance',
          actorRole: 'Warehouse Receiver',
          details: `Goods Receipt registered for PO #${input.poNumber}. Total received: ${totalReceived}, Accepted: ${totalAccepted}.`,
          systemSource: 'ProcureAI',
        },
      ],
      notes: input.notes,
      version: 1,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    this.grns.unshift(newGrn);
    if (idempotencyKey) {
      this.idempotencyCache.set(idempotencyKey, newGrn);
    }

    notifyGlobal('Goods Receipt Created', `GRN ${newGrn.grnNumber} generated for PO ${newGrn.poNumber}.`, 'System');

    return JSON.parse(JSON.stringify(newGrn));
  }

  async recordQualityInspection(
    id: string,
    inspections: QualityInspectionItemInput[],
    inspectorNotes?: string
  ): Promise<GoodsReceiptRecord> {
    await delay(env.mockDelayMs);
    const grn = this.findInternalRecord(id);
    if (!grn) throw new Error('Goods Receipt not found');

    inspections.forEach((insp) => {
      const item = grn.items.find((it) => it.id === insp.grnItemId);
      if (item) {
        if (insp.rejectedQuantity > item.receivedQuantity) {
          throw new Error(`Rejected quantity (${insp.rejectedQuantity}) cannot exceed received quantity (${item.receivedQuantity}).`);
        }
        item.acceptedQuantity = calculateAcceptedQuantity(item.receivedQuantity, insp.rejectedQuantity);
        item.rejectedQuantity = Math.max(0, insp.rejectedQuantity);
        item.pendingQuantity = calculatePendingQuantity(item.orderedQuantity, item.acceptedQuantity);
        item.rejectionReason = insp.rejectionReason;
        item.rejectionNotes = insp.rejectionNotes;
        item.qualityStatus = item.rejectedQuantity > 0 ? (item.acceptedQuantity > 0 ? 'PARTIALLY_PASSED' : 'FAILED') : 'PASSED';
      }
    });

    grn.totalAcceptedQuantity = grn.items.reduce((sum, it) => sum + it.acceptedQuantity, 0);
    grn.totalRejectedQuantity = grn.items.reduce((sum, it) => sum + it.rejectedQuantity, 0);
    grn.totalPendingQuantity = grn.items.reduce((sum, it) => sum + it.pendingQuantity, 0);
    grn.rejectionRatePercent = calculateRejectionRate(grn.totalReceivedQuantity, grn.totalRejectedQuantity);
    grn.qualityStatus = deriveOverallQualityStatus(grn.totalReceivedQuantity, grn.totalAcceptedQuantity, grn.totalRejectedQuantity);

    if (grn.totalRejectedQuantity > 0 && grn.totalAcceptedQuantity === 0) {
      grn.status = 'REJECTED';
    } else if (grn.totalRejectedQuantity > 0) {
      grn.status = 'PARTIALLY_ACCEPTED';
    } else {
      grn.status = 'ACCEPTED';
    }

    grn.threeWayMatchStatus =
      grn.totalRejectedQuantity > 0 || grn.totalPendingQuantity > 0
        ? 'QUANTITY_MISMATCH'
        : 'MATCHED';

    grn.version += 1;
    grn.updatedAt = new Date().toISOString();

    grn.auditHistory.unshift({
      id: `aud-${grn.id}-${Date.now()}`,
      grnId: grn.id,
      timestamp: grn.updatedAt,
      eventType: 'INSPECTION_RECORDED',
      actorId: 'usr-qa-01',
      actorName: grn.inspectorName || 'Quality Lead',
      actorRole: 'Quality Inspector',
      details: `Inspection recorded. Accepted: ${grn.totalAcceptedQuantity}, Rejected: ${grn.totalRejectedQuantity}. ${inspectorNotes || ''}`,
      systemSource: 'ProcureAI',
    });

    notifyGlobal('Inspection Recorded', `Quality inspection completed for GRN ${grn.grnNumber}.`, 'System');

    return JSON.parse(JSON.stringify(grn));
  }

  async acceptAllGoods(id: string, notes?: string): Promise<GoodsReceiptRecord> {
    await delay(env.mockDelayMs);
    const grn = this.findInternalRecord(id);
    if (!grn) throw new Error('Goods Receipt not found');

    grn.items.forEach((it) => {
      it.rejectedQuantity = 0;
      it.acceptedQuantity = it.receivedQuantity;
      it.pendingQuantity = calculatePendingQuantity(it.orderedQuantity, it.acceptedQuantity);
      it.qualityStatus = 'PASSED';
    });

    grn.totalAcceptedQuantity = grn.totalReceivedQuantity;
    grn.totalRejectedQuantity = 0;
    grn.totalPendingQuantity = grn.items.reduce((sum, it) => sum + it.pendingQuantity, 0);
    grn.rejectionRatePercent = 0;
    grn.qualityStatus = 'PASSED';
    grn.status = 'ACCEPTED';
    grn.threeWayMatchStatus = grn.totalPendingQuantity > 0 ? 'QUANTITY_MISMATCH' : 'MATCHED';
    grn.version += 1;
    grn.updatedAt = new Date().toISOString();

    grn.auditHistory.unshift({
      id: `aud-${grn.id}-${Date.now()}`,
      grnId: grn.id,
      timestamp: grn.updatedAt,
      eventType: 'ITEMS_ACCEPTED',
      actorId: 'usr-qa-01',
      actorName: 'John Miller',
      actorRole: 'Quality Inspector',
      details: `Full warehouse acceptance issued for ${grn.totalAcceptedQuantity} units. ${notes || ''}`,
      systemSource: 'ProcureAI',
    });

    notifyGlobal('Goods Accepted', `All items in GRN ${grn.grnNumber} accepted into plant inventory.`, 'System');

    return JSON.parse(JSON.stringify(grn));
  }

  async placeOnQualityHold(id: string, reason: string): Promise<GoodsReceiptRecord> {
    await delay(env.mockDelayMs);
    const grn = this.findInternalRecord(id);
    if (!grn) throw new Error('Goods Receipt not found');

    grn.status = 'QUALITY_HOLD';
    grn.qualityStatus = 'ON_HOLD';
    grn.version += 1;
    grn.updatedAt = new Date().toISOString();

    grn.auditHistory.unshift({
      id: `aud-${grn.id}-${Date.now()}`,
      grnId: grn.id,
      timestamp: grn.updatedAt,
      eventType: 'QUALITY_HOLD_APPLIED',
      actorId: 'usr-qa-01',
      actorName: 'John Miller',
      actorRole: 'Quality Inspector',
      details: `Shipment placed on Quality Hold. Justification: ${reason}`,
      systemSource: 'ProcureAI',
    });

    notifyGlobal('Quality Hold', `GRN ${grn.grnNumber} placed on quality hold. Downstream invoice blocked.`, 'System');

    return JSON.parse(JSON.stringify(grn));
  }

  async releaseFromQualityHold(id: string, notes?: string): Promise<GoodsReceiptRecord> {
    await delay(env.mockDelayMs);
    const grn = this.findInternalRecord(id);
    if (!grn) throw new Error('Goods Receipt not found');

    grn.status = 'UNDER_INSPECTION';
    grn.qualityStatus = 'PENDING_INSPECTION';
    grn.version += 1;
    grn.updatedAt = new Date().toISOString();

    grn.auditHistory.unshift({
      id: `aud-${grn.id}-${Date.now()}`,
      grnId: grn.id,
      timestamp: grn.updatedAt,
      eventType: 'QUALITY_STATUS_CHANGED',
      actorId: 'usr-qa-01',
      actorName: 'John Miller',
      actorRole: 'Quality Inspector',
      details: `Released from Quality Hold. ${notes || ''}`,
      systemSource: 'ProcureAI',
    });

    return JSON.parse(JSON.stringify(grn));
  }

  async syncGrnWithSap(id: string): Promise<{ success: boolean; sapGrnNumber: string; grn: GoodsReceiptRecord }> {
    await delay(env.mockDelayMs);
    const grn = this.findInternalRecord(id);
    if (!grn) throw new Error('Goods Receipt not found');

    grn.sapSyncStatus = 'SYNCED';
    grn.sapGrnNumber = grn.sapGrnNumber || `500090${Math.floor(1000 + Math.random() * 9000)}`;
    grn.updatedAt = new Date().toISOString();

    grn.auditHistory.unshift({
      id: `aud-${grn.id}-${Date.now()}`,
      grnId: grn.id,
      timestamp: grn.updatedAt,
      eventType: 'SAP_SYNC_SUCCESS',
      actorId: 'SAP_BAPI',
      actorName: 'SAP MIGO Service',
      actorRole: 'Integration Service',
      details: `Replicated Goods Receipt to SAP S/4HANA document #${grn.sapGrnNumber} (Movement 101).`,
      systemSource: 'SAP_BAPI',
    });

    notifyGlobal('SAP MIGO Success', `GRN ${grn.grnNumber} posted to SAP MIGO (${grn.sapGrnNumber}).`, 'System');

    return {
      success: true,
      sapGrnNumber: grn.sapGrnNumber,
      grn: JSON.parse(JSON.stringify(grn)),
    };
  }

  async cancelGrn(id: string, reason?: string): Promise<GoodsReceiptRecord> {
    await delay(env.mockDelayMs);
    const grn = this.findInternalRecord(id);
    if (!grn) throw new Error('Goods Receipt not found');

    grn.status = 'CANCELLED';
    grn.updatedAt = new Date().toISOString();

    grn.auditHistory.unshift({
      id: `aud-${grn.id}-${Date.now()}`,
      grnId: grn.id,
      timestamp: grn.updatedAt,
      eventType: 'GRN_CANCELLED',
      actorId: 'usr-buyer-01',
      actorName: 'David Sterling',
      actorRole: 'Procurement Officer',
      details: `Goods Receipt cancelled. Reason: ${reason || 'Administrative cancellation'}`,
      systemSource: 'ProcureAI',
    });

    return JSON.parse(JSON.stringify(grn));
  }

  /**
   * Retrieves Delivery Tracking records across all active Purchase Orders.
   */
  async getDeliveries(params: {
    search?: string;
    plant?: string;
    deliveryStatus?: string;
    isDelayed?: boolean;
    page?: number;
    pageSize?: number;
  }): Promise<{ items: DeliveryTrackingRecord[]; total: number; page: number; pageSize: number }> {
    await delay(env.mockDelayMs);

    const deliveryRecords: DeliveryTrackingRecord[] = MOCK_PURCHASE_ORDERS.map((po, i) => {
      const linkedGrns = this.grns.filter((g) => g.poId === po.id || g.poNumber === po.poNumber);
      const totalOrdered = po.items.reduce((sum, it) => sum + it.quantity, 0);
      const totalReceived = linkedGrns.reduce((sum, g) => sum + g.totalReceivedQuantity, 0);
      const totalAccepted = linkedGrns.reduce((sum, g) => sum + g.totalAcceptedQuantity, 0);
      const totalRejected = linkedGrns.reduce((sum, g) => sum + g.totalRejectedQuantity, 0);
      const totalPending = calculatePendingQuantity(totalOrdered, totalAccepted);

      const promisedDeliveryDate = po.deliveryDate || '2026-10-30';
      const lastReceiptDate = linkedGrns[0]?.receivedDate;
      const isFullyDelivered = totalOrdered > 0 && totalAccepted >= totalOrdered;

      const { isDelayed, delayDays } = detectDeliveryDelay(promisedDeliveryDate, lastReceiptDate, isFullyDelivered);

      const deliveryStatus = deriveDeliveryStatus({
        orderedQuantity: totalOrdered,
        receivedQuantity: totalReceived,
        acceptedQuantity: totalAccepted,
        rejectedQuantity: totalRejected,
        promisedDeliveryDate,
        actualReceiptDate: lastReceiptDate,
      });

      return {
        id: `deliv-${po.id}`,
        poId: po.id,
        poNumber: po.poNumber,
        sapPoNumber: po.sapPoNumber,
        vendorId: po.vendorId,
        vendorName: po.vendorName,
        vendorCode: `VND-${po.vendorId.toUpperCase()}`,
        plantCode: 'PLANT-1000',
        poDate: po.createdAt || '2026-09-01',
        promisedDeliveryDate,
        lastReceiptDate,
        totalOrderedQuantity: totalOrdered,
        totalReceivedQuantity: totalReceived,
        totalAcceptedQuantity: totalAccepted,
        totalRejectedQuantity: totalRejected,
        totalPendingQuantity: totalPending,
        deliveryStatus,
        isDelayed,
        delayDays,
        grnIds: linkedGrns.map((g) => g.id),
        latestGrnNumber: linkedGrns[0]?.grnNumber,
        threeWayMatchStatus: totalRejected > 0 || totalPending > 0 ? 'QUANTITY_MISMATCH' : 'MATCHED',
        invoiceStatus: i % 4 === 0 ? 'Invoice Received' : 'Pending Receipt',
        materialSummaries: po.items.map((it) => it.materialName).join(', '),
      };
    });

    let results = deliveryRecords;
    if (params.search) {
      const q = params.search.toLowerCase().trim();
      results = results.filter(
        (d) =>
          d.poNumber.toLowerCase().includes(q) ||
          d.vendorName.toLowerCase().includes(q) ||
          (d.latestGrnNumber && d.latestGrnNumber.toLowerCase().includes(q)) ||
          d.materialSummaries.toLowerCase().includes(q)
      );
    }

    if (params.deliveryStatus && params.deliveryStatus !== 'ALL') {
      results = results.filter((d) => d.deliveryStatus === params.deliveryStatus);
    }

    if (params.isDelayed !== undefined) {
      results = results.filter((d) => d.isDelayed === params.isDelayed);
    }

    const page = params.page || 1;
    const pageSize = params.pageSize || 25;
    const startIndex = (page - 1) * pageSize;
    const paginated = results.slice(startIndex, startIndex + pageSize);

    return {
      items: JSON.parse(JSON.stringify(paginated)),
      total: results.length,
      page,
      pageSize,
    };
  }

  async getVendorDeliveryPerformance(vendorId: string): Promise<VendorDeliveryPerformance> {
    await delay(env.mockDelayMs);
    const deliveriesRes = await this.getDeliveries({ pageSize: 500 });
    const vendorDeliveries = deliveriesRes.items.filter((d) => d.vendorId === vendorId);
    const vendorName = vendorDeliveries[0]?.vendorName || 'Apex Technologies Ltd';
    const vendorCode = vendorDeliveries[0]?.vendorCode || 'VND-APEX-01';

    return calculateVendorPerformance(vendorId, vendorName, vendorCode, this.grns, deliveriesRes.items);
  }

  async updateGoodsReceipt(id: string, data: Partial<GoodsReceiptRecord>): Promise<GoodsReceiptRecord> {
    await delay(env.mockDelayMs);
    const grn = this.grns.find((g) => g.id === id || g.grnNumber === id);
    if (!grn) throw new Error(`Goods Receipt ${id} not found`);
    Object.assign(grn, data);
    grn.version = (grn.version || 1) + 1;
    grn.updatedAt = new Date().toISOString();
    return JSON.parse(JSON.stringify(grn));
  }

  async deleteGoodsReceipt(id: string): Promise<boolean> {
    await delay(env.mockDelayMs);
    const initialLen = this.grns.length;
    this.grns = this.grns.filter((g) => g.id !== id && g.grnNumber !== id);
    return this.grns.length < initialLen;
  }
}

export const mockGoodsReceiptService = new MockGoodsReceiptService();
